import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, MapPin } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";

const HERO_IMG = "/assets/hero-factory.png";
const FALLBACK_IMG = "https://images.unsplash.com/photo-1598299803204-b73796f43289?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="beranda" ref={ref} data-testid="hero-section"
      className="relative min-h-screen flex items-center blueprint-grid overflow-hidden bg-[#0B1220]">
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-sky-500/15 blur-3xl" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-wood-cedar/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-16 lg:pt-32 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
        <motion.div style={{ opacity: fade }} className="lg:col-span-6 xl:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold flex items-center gap-2"
            data-testid="hero-eyebrow"
          >
            <MapPin size={13} /> {t.hero.eyebrow}
          </motion.p>

          <h1 className="mt-6 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.04]" data-testid="hero-headline">
            {t.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 1 ? <span className="text-sky-400">{line}</span> : i === 2 ? <span className="text-wood-cedar">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href={waLink(t.hero.waMsg)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-hero-btn"
              className="group inline-flex items-center gap-3 bg-sky-500 text-white font-semibold px-7 py-4 rounded-full hover:bg-sky-400 transition-colors shadow-[0_16px_40px_-12px_rgba(56,189,248,0.45)]">
              <MessageCircle size={19} className="group-hover:rotate-12 transition-transform" />
              {t.hero.ctaWa}
            </a>
            <a href="#katalog" data-testid="hero-catalog-btn"
              className="inline-flex items-center gap-2 border-2 border-slate-600 text-slate-100 font-semibold px-7 py-[14px] rounded-full hover:border-sky-400 hover:text-sky-400 transition-colors">
              {t.hero.ctaCatalog} <ArrowDown size={17} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            className="mt-12 grid grid-cols-3 gap-4 max-w-lg"
            data-testid="hero-stats"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} className={`${i > 0 ? "border-l border-slate-700 pl-4" : ""}`}>
                <p className="font-display font-extrabold text-xl sm:text-2xl text-white">{s.value}</p>
                <p className="text-xs sm:text-sm text-slate-400 mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="clip-industrial overflow-hidden shadow-[0_40px_80px_-24px_rgba(0,0,0,0.7)] border-4 border-slate-700/60">
              <img
                src={HERO_IMG}
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                alt="Pabrik kayu CV. Sinar Al Mubarokah — gudang kayu olahan dengan tim profesional"
                className="w-full h-[320px] sm:h-[440px] lg:h-[540px] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <div className="absolute -bottom-5 left-5 sm:left-8 bg-[#101B2E] rounded-2xl shadow-xl border border-slate-700 px-5 py-3.5 flex items-center gap-3" data-testid="hero-image-badge">
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 animate-pulse-ring" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-emerald-500" />
              </span>
              <p className="text-sm font-semibold text-slate-100">{t.hero.badge}</p>
            </div>
            <div className="absolute -top-4 -right-3 sm:-right-5 bg-sky-500 text-white font-mono text-[10px] sm:text-xs tracking-widest uppercase px-4 py-2 rounded-full rotate-3 shadow-lg">
              Est. Cilincing · JKT
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
